/**
 * Module initialization orchestrator
 */

import { languageManager } from '../lang/language-manager.js';
import { searchEngine } from '../search/search-engine.js';
import { filterManager } from '../filters/filter-manager.js';
import { fileListManager } from '../files/file-list.js';
import { lazyLoadManager } from '../files/file-list.lazy-load.js';
import { searchUI } from '../ui/search-ui.js';
import { paginationManager } from '../ui/pagination.js';
import { filterController } from '../filters/filter-controller.js';
import { dateFiltersManager } from '../filters/date-filters.js';
import FiltersToggleManager from '../filters/filters-toggle.js';
import { cardController } from '../cards/card-controller.js';
import { cardEventsManager } from '../cards/card-events.js';
import { appInitializationManager } from '../app/app-initialization.js';
import { bootstrapManager } from '../ui/bootstrap-manager.js';
import { tabManager } from '../ui/tab-manager/index.js';
import { dateParser } from '../datetime/date-parser.js';
import { dateFormatter } from '../datetime/date-formatter.js';
import { dateValidator } from '../datetime/date-validator.js';
import { timezoneManager } from '../datetime/timezone-manager.js';
import { initializeSearchOrchestrator } from '../app/search-orchestrator.js';
import { getFilterElements, getFileItemTemplate } from './file-utils.js';
import { initializeAddToHomeScreen } from '../app/add-to-homescreen.js';
import { initAttentionPulse } from '../ui/attention-pulse.js';

export async function initializeModules(ctx, {
    initializePhase10,
    connectModules,
    config,
    debugMode
}) {
    try {
        // ✅ CRITICAL: Register component status with weight (percentage)
        if(window.ComponentLoadingStatus){
            window.ComponentLoadingStatus.register('modulesInitializer', 'loading', 'Initializing modules...', 8);
        }
        
        // Initialize storage first (already a singleton)

        // Initialize Phase 10 modules
        const phase10Success = await initializePhase10();
        if (!phase10Success) {
            if (debugMode === '1') {
                console.warn('[main-init] Phase 10 initialization had issues, continuing with fallback...');
            }
        }

        // Initialize language manager
        languageManager.init();

        // Initialize search engine
        searchEngine.init({
            debounceDelay: config.debounceDelay,
            maxHistorySize: 50
        });

        // Initialize filter manager
        const filterElements = getFilterElements();
        filterManager.init(filterElements);
        // Align FilterManager debounce with app config
        try {
            filterManager.searchDebounceDelay = config.debounceDelay;
        } catch(_) { /* noop */ }

        // Initialize date filters manager (ensure immediate reaction to date input)
        try {
            dateFiltersManager.initialize({ debounceDelay: 250, autoSetup: true });
        } catch(_) { /* noop */ }

        // Initialize Litepicker wrapper
        try {
            const drp = new window.LiteDateRangePicker({
                selector: '#date-range',
                minDate: (window.__idbMetaCache && window.__idbMetaCache.date_min) || null,
                maxDate: (window.__idbMetaCache && window.__idbMetaCache.date_max) || null
            });
            drp.init();
            window.__drp = drp;
            
            // Apply dates from URL after initialization
            // This ensures the date range picker shows dates from URL on page load
            try {
                const urlParams = new URLSearchParams(window.location.search);
                const dateFrom = urlParams.get('date-from') || urlParams.get('dateFrom') || '';
                const dateTo = urlParams.get('date-to') || urlParams.get('dateTo') || '';
                
                if (dateFrom || dateTo) {
                    const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
                    const toDate = dateTo ? new Date(dateTo + 'T00:00:00') : null;
                    if (drp && typeof drp.applyRange === 'function') {
                        drp.applyRange(fromDate, toDate, 'restore');
                    }
                }
            } catch(e) {
                if (debugMode === '1') {
                    console.warn('[modules-initializer] Could not apply initial dates to LiteDateRangePicker:', e);
                }
            }
        } catch(_) { /* noop */ }

        // Filters toggle disabled - filters are always visible now
        // try {
        //     const filtersToggle = new FiltersToggleManager();
        //     filtersToggle.initialize();
        //     filtersToggle.bindCollapseSync();
        // } catch(_) { /* noop */ }

        // Feature-discovery pulse (filter button etc.) — fires after loader hides
        try { initAttentionPulse(); } catch(_) { /* noop */ }

        // Initialize filter controller (creates dynamic category filters)
        // Categories now come from filter_metadata in IndexedDB, not from CATEG object
        // Get translations from TranslationRegistry in old format for compatibility
        let translationsObj = {};
        if (window.TranslationRegistry && typeof window.TranslationRegistry.getAllTranslations === 'function') {
            translationsObj = window.TranslationRegistry.getAllTranslations() || {};
        }
        
        filterController.initialize({
            categories: {}, // Empty - filters loaded from filter_metadata via refreshFiltersFromMetadata()
            language: window.LANG_FROM_SERVER || 'EN',
            translations: translationsObj,
            startCategories: {}
        });

        // Initialize file list manager
        fileListManager.init({
            containerSelector: '#file-list-container',
            itemTemplate: getFileItemTemplate()
        });

        // Initialize lazy load manager for infinite scroll
        const lazyInitResult = lazyLoadManager.init();

        // Initialize search UI
        searchUI.init({
            searchInputSelector: '#fnd_searchbox',
            suggestionsSelector: '#search-suggestions',
            clearButtonSelector: '#clear-search'
        });

        // Initialize pagination
        paginationManager.init({
            containerSelector: '#pagination-container',
            pageSize: config.pageSize
        });

        // Initialize Phase 7 modules - Card Management
        await cardController.initialize();
        await cardEventsManager.initialize();

        // Initialize Phase 8 modules - App Initialization & Bootstrap
        await appInitializationManager.initializeApp();
        await bootstrapManager.initialize();
        await tabManager.initializeTabEvents();
        
        // Initialize Add to Home Screen functionality
        try {
            initializeAddToHomeScreen();
        } catch (e) {
            if (debugMode === '1') {
                console.warn('[modules-initializer] Could not initialize Add to Home Screen:', e);
            }
        }
        
        // Ensure date range picker is synced with URL after all initialization
        // This is a fallback in case dates weren't applied during initialization
        try {
            if (window.__drp && typeof window.__drp.applyRange === 'function') {
                const urlParams = new URLSearchParams(window.location.search);
                const dateFrom = urlParams.get('date-from') || urlParams.get('dateFrom') || '';
                const dateTo = urlParams.get('date-to') || urlParams.get('dateTo') || '';
                
                // Check if dates are already set in hidden inputs
                const dateFromInput = document.querySelector('.filter-date-from');
                const dateToInput = document.querySelector('.filter-date-to');
                const hasDatesInInputs = (dateFromInput && dateFromInput.value) || (dateToInput && dateToInput.value);
                
                // Only apply if dates are in URL but not in inputs (or if dates match URL)
                if ((dateFrom || dateTo) && (!hasDatesInInputs || (dateFromInput && dateFromInput.value === dateFrom) || (dateToInput && dateToInput.value === dateTo))) {
                    const fromDate = dateFrom ? new Date(dateFrom + 'T00:00:00') : null;
                    const toDate = dateTo ? new Date(dateTo + 'T00:00:00') : null;
                    window.__drp.applyRange(fromDate, toDate, 'restore');
                }
            }
        } catch(e) {
            if (debugMode === '1') {
                console.warn('[modules-initializer] Could not sync date range picker after initialization:', e);
            }
        }

        // Initialize Phase 9 modules - Date & Time Management
        dateParser.initialized = true;
        dateFormatter.initialized = true;
        dateValidator.initialized = true;
        timezoneManager.initialized = true;

        // Initialize notifications system (with delay to ensure modules are loaded)
        setTimeout(() => {
            if (window.NotificationManager && window.SERVER_AUTH_STATUS && window.SERVER_AUTH_STATUS.isAuthenticated) {
                try {
                    window.NotificationManager.init();
                } catch (error) {
                    console.error('[main-init] Failed to initialize notifications:', error);
                }
            }
        }, 100);

        // Connect modules
        connectModules();
        
        // ✅ CRITICAL: Update component status to 'ready'
        if(window.ComponentLoadingStatus){
            let caption = 'Modules initialized';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
              const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
              caption = window.TranslationRegistry.translate('loader_stage_modules_initialized', lang);
            }
            window.ComponentLoadingStatus.update('modulesInitializer', 'ready', caption);
        }
    } catch (error) {
        if (debugMode === '1') {
            console.error('[main-init] ERROR in initializeModules():', error);
        }
        
        // ✅ CRITICAL: Update component status to 'error' on failure
        if(window.ComponentLoadingStatus){
            let caption = 'Module initialization failed';
            if (window.TranslationRegistry && typeof window.TranslationRegistry.translate === 'function') {
              const lang = window.LanguageManager ? window.LanguageManager.getCurrentLanguage() : (window.LANG || 'EN');
              caption = window.TranslationRegistry.translate('loader_stage_module_init_failed', lang);
            }
            window.ComponentLoadingStatus.update('modulesInitializer', 'error', caption);
        }
        
        throw error;
    }
}

